/**
 * Unified Report Templates Hook
 * Loads templates from policy packs with fallback to config files
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getProject } from '../api/projects';
import { getPolicyPack } from '../api/policy';
import { extractReportTemplatesFromPolicyPack } from '../utils/policyPackLoader';
import { convertPolicyPackTemplatesToReportTemplates } from '../utils/templateConverter';
import { ReportTemplate } from '../types/reports';
import { useWorkspace } from './useWorkspace';

interface UseReportTemplatesOptions {
  fallbackTemplates?: ReportTemplate[];
  enabled?: boolean;
}

/**
 * Hook to load report templates from policy pack or fallback
 */
export function useReportTemplates(options: UseReportTemplatesOptions = {}) {
  const { projectId } = useWorkspace();
  const { fallbackTemplates = [], enabled = true } = options;

  // Load project to get policy pack ID
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(projectId!),
    enabled: !!projectId && enabled,
  });

  // Load policy pack if project has one
  const { data: policyPack } = useQuery({
    queryKey: ['policy-pack', project?.policy_pack_id],
    queryFn: () => getPolicyPack(project!.policy_pack_id!),
    enabled: !!project?.policy_pack_id && enabled,
  });

  // Extract and convert templates
  const templates = useMemo(() => {
    if (policyPack) {
      // Policy pack can have data in rules or policy_data field
      const policyData = policyPack.rules || policyPack.policy_data || {};
      
      // Extract report templates from policy pack
      const policyPackTemplates = extractReportTemplatesFromPolicyPack(policyData);
      
      if (policyPackTemplates.length > 0) {
        console.log('[useReportTemplates] Loaded templates from policy pack:', policyPackTemplates.length);
        // Convert to ReportTemplate format with icons and colors
        const convertedTemplates = convertPolicyPackTemplatesToReportTemplates(policyPackTemplates);
        return convertedTemplates;
      }
    }
    
    // Fallback to provided templates
    if (fallbackTemplates.length > 0) {
      console.log('[useReportTemplates] Using fallback templates:', fallbackTemplates.length);
      return fallbackTemplates;
    }
    
    return [];
  }, [policyPack, fallbackTemplates]);

  // Extract report types from templates for dropdown
  const reportTypes = useMemo(() => {
    if (templates.length > 0) {
      return [
        { value: '', label: 'Select type...' },
        ...templates.map(t => ({ value: t.type, label: t.name })),
        { value: 'custom', label: 'Custom Report' },
      ];
    }
    return [];
  }, [templates]);

  return {
    templates,
    reportTypes,
    isLoading: !projectId || (!!project?.policy_pack_id && !policyPack),
  };
}

